// Diary utility functions for server deployment compatibility

// Fallback clipboard function for non-HTTPS environments
export const fallbackCopyText = (text) => {
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (!successful) {
      // Store in session storage as last resort
      sessionStorage.setItem('diaryText', text);
      console.log('Text stored in session storage');
    }
    return successful;
  } catch (err) {
    console.error('Fallback copy failed:', err);
    // Store in session storage as last resort
    try {
      sessionStorage.setItem('diaryText', text);
      return true;
    } catch (storageErr) {
      console.error('Session storage failed:', storageErr);
      return false;
    }
  }
};

// Enhanced clipboard copy with fallback
export const copyToClipboard = async (text, successMessage = 'Text copied!', fallbackMessage = 'Text ready to copy manually') => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      alert(`📋 ${successMessage}`);
      return true;
    } catch (err) {
      console.error('Clipboard API failed:', err);
      const success = fallbackCopyText(text);
      alert(`📋 ${success ? fallbackMessage : 'Copy failed - text stored in session'}`);
      return success;
    }
  } else {
    const success = fallbackCopyText(text);
    alert(`📋 ${success ? fallbackMessage : 'Copy failed - text stored in session'}`);
    return success;
  }
};

// Memory cleanup utility for canvas
export const cleanupCanvas = (canvas) => {
  if (canvas && canvas.getContext) {
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    canvas.width = 1;
    canvas.height = 1;
  }
};

// Mobile detection utility
export const isMobile = () => {
  return window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Safe canvas image generation with timeout
export const generateCanvasImage = (canvasGeneratorFn, timeoutMs = 15000) => {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('Image generation timeout'));
    }, timeoutMs);

    try {
      const result = canvasGeneratorFn();
      if (result instanceof Promise) {
        result
          .then((canvas) => {
            clearTimeout(timeout);
            resolve(canvas);
          })
          .catch((error) => {
            clearTimeout(timeout);
            reject(error);
          });
      } else {
        clearTimeout(timeout);
        resolve(result);
      }
    } catch (error) {
      clearTimeout(timeout);
      reject(error);
    }
  });
};

// Safe blob download with fallback
export const downloadBlob = (blob, filename, fallbackText = null) => {
  try {
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      // IE/Edge
      window.navigator.msSaveOrOpenBlob(blob, filename);
      return true;
    } else {
      // Modern browsers
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
      return true;
    }
  } catch (error) {
    console.error('Download failed:', error);
    if (fallbackText) {
      copyToClipboard(fallbackText, 'Download failed - text copied instead', 'Download failed - text ready to copy');
    }
    return false;
  }
};

// Copy PNG image to clipboard
export const copyImageToClipboard = async (imageSource, successMessage = 'Image copied to clipboard!', errorMessage = 'Failed to copy image') => {
  try {
    let blob;
    
    if (imageSource instanceof HTMLCanvasElement) {
      // Canvas element
      blob = await new Promise(resolve => imageSource.toBlob(resolve, 'image/png'));
    } else if (imageSource instanceof HTMLImageElement) {
      // Image element - convert to canvas first
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = imageSource.naturalWidth;
      canvas.height = imageSource.naturalHeight;
      ctx.drawImage(imageSource, 0, 0);
      blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    } else if (typeof imageSource === 'string') {
      // URL - fetch and convert
      const response = await fetch(imageSource);
      blob = await response.blob();
    } else {
      throw new Error('Unsupported image source type');
    }

    if (!blob) {
      throw new Error('Failed to create image blob');
    }

    // Check if Clipboard API is available
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob })
      ]);
      alert(`📋 ${successMessage}`);
      return true;
    } else {
      // Fallback: download the image
      downloadBlob(blob, `homework_diary_${Date.now()}.png`);
      alert('📱 Image downloaded! You can now share it manually.');
      return false;
    }
  } catch (error) {
    console.error('Copy image error:', error);
    alert(`❌ ${errorMessage}: ${error.message}`);
    return false;
  }
};

// Copy homework diary image from URL
export const copyHomeworkImage = async (imageUrl) => {
  return await copyImageToClipboard(
    imageUrl,
    'Homework diary image copied! You can now paste it anywhere.',
    'Failed to copy homework diary image'
  );
};

// API error handler
export const handleApiError = (error, defaultMessage = 'Operation failed') => {
  console.error('API Error:', error);
  
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please check your connection and try again.';
  } else if (error.response?.status === 404) {
    return 'Resource not found. Please contact administrator.';
  } else if (error.response?.status >= 500) {
    return 'Server error. Please try again later.';
  } else if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.message) {
    return error.message;
  } else {
    return defaultMessage;
  }
};